/**
 * Errors and rejections that are noise rather than defects. Asked as one question so the
 * Sentry `beforeSend` filter and the global unhandled-rejection toast cannot drift apart —
 * an event worth hiding from one is worth hiding from the other.
 *
 * Each predicate takes the thrown value, so the same question answers for a promise
 * rejection (`event.reason`) and for a thrown error (`hint.originalException`).
 */

/**
 * True for a rejection that is only a cancelled request — an `AbortController.abort()`
 * rejecting whatever was still in flight.
 *
 * Nothing in this app aborts a request whose result still matters. Every
 * `AbortController` here supersedes a request the user has already moved past
 * (`Robots.vm.ts` and `useLiveRobotStreaming.ts`, both of which already swallow the
 * AbortError at their own call site), and MapLibre aborts a map tile's fetch the instant
 * the tile scrolls out of view (`TileManager._removeTile` -> `abortTile`). That last one
 * has no call site of ours to catch it, so it surfaced at
 * `window.onunhandledrejection` — where an ordinary map pan toasted the operator, popped
 * Sentry's crash-report dialog and filed PORTAL-WEB-X7. An abort nobody awaited is not
 * actionable by construction: whoever aborted it is the one who stopped caring about the
 * answer.
 *
 * Deliberately keyed on `name`, not on the message: "signal is aborted without reason" is
 * the browser's wording and a browser update may change it, while `AbortError` is spec.
 */
export function isAbortRejection(reason: unknown): boolean {
	return typeof reason === "object" && reason !== null && (reason as { name?: unknown }).name === "AbortError";
}

/**
 * True for an error the axios response interceptor has already dealt with end to end —
 * it decided the user-facing message, decided whether Sentry should hear about it, and
 * marked the error with `_interceptorHandled` on its way back out (see `api/client.ts`).
 *
 * The session-expiry 401 is the case that matters. The interceptor clears the tokens,
 * logs the user out, toasts "Session expired. Please log in again." and deliberately does
 * *not* report — an expired token is the system working, not a defect. It still rejects
 * the promise so call sites can unwind, and a call site with no `catch` lets that
 * rejection reach `window.onunhandledrejection`, where Sentry's own global handler files
 * it as an unhandled error anyway and `beforeSend` pops the crash-report dialog on top of
 * the toast (PORTAL-WEB-XC: a tab left open for three days, on the Submit Jobs form).
 *
 * So the interceptor's "don't report this" only holds while every call site catches. Asked
 * here instead, it holds by construction — the same reasoning that moved the MapLibre
 * abort into `isAbortRejection` above.
 *
 * Keyed on the flag the interceptor sets, not on the status code, so the answer stays
 * wherever the handling decision was actually made. `beforeSend` pairs this with a
 * `mechanism.handled` check, since the interceptor's *deliberate* reports (network
 * failure, 403, 5xx) are captured explicitly with `handled: true` and must survive.
 */
export function isInterceptorHandled(reason: unknown): boolean {
	return (
		typeof reason === "object" &&
		reason !== null &&
		(reason as { _interceptorHandled?: unknown })._interceptorHandled === true
	);
}

/**
 * True for `@hello-pangea/dnd`'s own `RbdInvariant` — the "Invariant failed" the library
 * throws out of an auto-scroll frame that lands after the drag it belonged to has already
 * ended ("Cannot scroll when there is no drag", `dnd.esm.js`).
 *
 * The library treats this as benign by construction: its `<ErrorBoundary>` binds a window
 * `error` listener that calls `event.preventDefault()` on any `RbdInvariant`, so by design
 * nobody is meant to see one. Sentry's `browserApiErrors` integration wraps
 * `requestAnimationFrame`, which is *inside* that frame — it captures the throw before the
 * window `error` event the boundary would have swallowed, so the suppression never gets a
 * say. The drag is already finished when this fires, so nothing is lost; the only
 * consequence was that `beforeSend` popped the crash-report dialog at a user mid-import
 * (PORTAL-WEB-XF, on the Bulk wizard's drag-to-assign cards). Same defect as the MapLibre
 * abort in `isAbortRejection`, reached by a different route.
 *
 * Keyed on the message, unlike the two predicates above, because it has to be: the class
 * is `class RbdInvariant extends Error {}` with no `name` of its own, so `name` is plain
 * `"Error"` and `constructor.name` does not survive minification. The message is safe to
 * key on here for the reason a browser-worded one is not — it is the library's own
 * compile-time constant (`const prefix$1 = "Invariant failed"`), pinned with the version
 * in `package.json`, not a string some future browser update rewords. `tiny-invariant`
 * throws the same text, but in this bundle it only reaches us through `css-box-model`,
 * which is itself a dnd dependency; no app code throws an invariant.
 *
 * Narrow on purpose: this drops only the frame-timing race, and only when Sentry captured
 * it automatically. A dnd invariant that breaks a drag surfaces as a React render error
 * through `componentDidCatch`, which is a different path and still reported.
 */
export function isDragInvariant(reason: unknown): boolean {
	if (typeof reason !== "object" || reason === null) return false;
	const { message } = reason as { message?: unknown };
	return typeof message === "string" && message.startsWith("Invariant failed");
}
